import { Box, Typography } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { useTheme } from 'contexts/Theme/Theme';
import * as d3 from 'd3';
import { formatValue, getUrl, transformValue } from 'helpers';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';

const { axesMultiplier, barGap, dayLabels, handleDragIndicatorWidth, handleHeight, height, margin } = setup.charts;

export default function TimelineChart({
    animate,
    axes,
    bars,
    controlLines,
    decimals,
    multiplier,
    hurricaneBands,
    hurricaneLines,
    periods,
    variance,
    width,
    onBarClick,
    onControlLineDrag,
}: TimelineChartProps) {
    const [node, setNode] = useState(d3.select(null));
    const game = useGame();
    const settings = useSettings();
    const theme = useTheme();

    const canvasRef = useCallback((ref: any) => {
        const newNode = d3.select(ref);
        setNode(newNode);
    }, []);

    const chartRect = useMemo(() => {
        return new DOMRect(
            margin.left,
            margin.top,
            Math.max(0, width - margin.left - margin.right),
            height - margin.top - margin.bottom
        );
    }, [width]);

    const scaleX = useMemo(() => {
        const maxValue = axes.x.max || Infinity;
        const values = _.range(bars.length)
            .map(index => index)
            .filter(value => value <= maxValue);
        return d3.scaleBand<number>().domain(values).range([0, chartRect.width]);
    }, [axes, bars, chartRect]);

    const scaleY = useMemo(() => {
        const barValues = bars.map(bar => transformValue(bar.value, multiplier, decimals));
        const controlValues = controlLines?.map(control => (control.value || 0) * multiplier) || [];
        const hurricaneValues = hurricaneBands || hurricaneLines ? game.hurricaneBands.red.map(day => day[1]) : [];
        const maxValue = axes.y.max || Math.max(0, ...barValues, ...controlValues, ...hurricaneValues);
        const maxY = Math.ceil(maxValue * axesMultiplier);

        return d3.scaleLinear().domain([0, maxY]).range([chartRect.height, 0]);
    }, [
        axes,
        bars,
        chartRect,
        controlLines,
        decimals,
        game.hurricaneBands,
        hurricaneBands,
        hurricaneLines,
        multiplier,
    ]);

    const barsData = useMemo<TimelineChartBarData[]>(() => {
        return bars.map((bar, index) => {
            const value = transformValue(bar.value, multiplier, decimals);
            return {
                animate: animate !== undefined && index >= animate && !settings.debugChart,
                color: bar.color || setup.colors.peach,
                opacity:
                    (bar.opacity !== 0 && hurricaneBands ? 0.85 : 0) ||
                    (bar.opacity !== undefined ? bar.opacity : 1) ||
                    (settings.debugChart ? 0.3 : 0),
                stroke: bar.opacity !== 0 && hurricaneBands ? 1 : 0,
                value,
                x: scaleX(index) || 0,
                y: scaleY(value),
            };
        });
    }, [animate, bars, decimals, hurricaneBands, multiplier, settings.debugChart, scaleX, scaleY]);

    const barStyles = useMemo(() => {
        const width = barsData.length ? chartRect.width / barsData.length : 0;
        const gap = Math.min(barGap, width / barGap);
        return {
            gap,
            width: width - gap,
        };
    }, [barsData, chartRect.width]);

    const getHurricaneBandArea = useMemo(() => {
        return d3
            .area()
            .curve(d3.curveCardinal.tension(0.2))
            .x((_, i) => (scaleX(i) || 0) + (i ? barStyles.width + barStyles.gap : 0) + chartRect.left)
            .y1(d => scaleY(d[0]) + chartRect.top)
            .y0(d => scaleY(d[1]) + chartRect.top);
    }, [barStyles, chartRect, scaleX, scaleY]);

    const periodLabels = useMemo(() => {
        if (!periods) {
            return null;
        }

        return _.range(periods.count || 1).map(index => `${periods.label} ${index + 1}`);
    }, [periods]);

    useEffect(() => {
        // Empty axes container
        const container = node.select('.timeline-chart-axes');
        container.selectChildren().remove();

        // Add X axis
        const axisX = d3
            .axisBottom(scaleX)
            .tickFormat((_, i) => (axes.x.label === 'Days' ? dayLabels[i % dayLabels.length] : (i + 1).toString()))
            .tickSizeInner(0)
            .tickSizeOuter(0)
            .tickPadding(10);

        container
            .append('g')
            .call(axisX)
            .call(d => d.selectAll('.tick text').attr('transform', `translate(${barStyles.gap / 2}, 0)`))
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.bottom})`)
            .style('font-size', '10px');

        container
            .append('text')
            .attr('fill', theme.darkMode ? setup.colors.white : setup.colors.black)
            .attr('text-anchor', 'start')
            .attr('x', chartRect.right + 10)
            .attr('y', chartRect.bottom + 5)
            .style('font-size', '12px')
            .text(axes.x.label);

        // Add Y axis
        const axisY = d3.axisLeft(scaleY).tickSizeInner(0).tickSizeOuter(0).tickPadding(10);

        container
            .append('g')
            .call(axisY)
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.top})`)
            .style('font-size', '12px');

        container
            .append('text')
            .attr('fill', theme.darkMode ? setup.colors.white : setup.colors.black)
            .attr('text-anchor', 'middle')
            .attr('x', chartRect.left)
            .attr('y', 12)
            .style('font-size', '12px')
            .text(axes.y.label);
    }, [axes, barStyles, chartRect, node, periods, theme.darkMode, scaleX, scaleY]);

    useEffect(() => {
        // Empty bars container
        const container = node.select('.timeline-chart-bars');
        container.selectChildren().remove();

        if (!barsData.length) {
            return;
        }

        // Add elements
        const elements = container
            .selectAll('rect')
            .data(barsData)
            .enter()
            .append('rect')
            .attr('x', d => Math.round(d.x + chartRect.left + barStyles.gap))
            .attr('y', d => Math.round(d.animate ? chartRect.bottom : d.y + chartRect.top))
            .attr('height', d => Math.round(d.animate ? 0 : chartRect.height - d.y + 1))
            .attr('width', barStyles.width)
            .style('stroke', setup.colors.black)
            .style('stroke-width', d => d.stroke)
            .style('fill', d => d.color)
            .style('opacity', d => d.opacity)
            .style('cursor', d => (d.opacity ? 'pointer' : ''))
            .attr('data-tooltip-id', 'chart-tooltip')
            .attr('data-tooltip-html', (d, i) =>
                !d.opacity
                    ? ''
                    : `<div style="color: ${setup.colors.black}; display: flex; flex-direction: column;">
                                                            <span>
                                                                ${axes.x.tooltipLabel || axes.x.label}:
                                                                <span style="font-weight: bold;">
                                                                    ${axes.x.tooltipPrefix || ''}${i + 1}${
                          axes.x.tooltipSuffix || ''
                      }
                                                                </span>
                                                            </span>
                                                            <span>
                                                                ${axes.y.tooltipLabel || axes.y.label}:
                                                                <span style="font-weight: bold;">
                                                                    ${
                                                                        axes.y.tooltipPrefix || ''
                                                                    }${d.value.toLocaleString()}${
                          axes.y.tooltipSuffix || ''
                      }
                                                                </span>
                                                            </span>
                                                        </div>`
            )
            .on('click', (_, d) => onBarClick?.(d));

        // Animate
        if (animate !== undefined && !settings.debugChart) {
            const count =
                barsData.filter(barData => barData.opacity === undefined || barData.opacity > 0).length - animate;
            const stagger = 7000 / count;
            elements
                .transition()
                .delay((_, i) => stagger * (i - animate) + 3000)
                .duration(Math.min(count / 4, 4) * stagger)
                .attr('height', d => Math.round(chartRect.height - d.y + 1))
                .attr('y', d => Math.round(d.y + chartRect.top));
        }
    }, [animate, axes, barsData, barStyles, chartRect, node, onBarClick, settings.debugChart]);

    useEffect(() => {
        // Empty control lines container
        const container = node.select('.timeline-chart-control-lines');
        container.selectChildren().remove();

        if (!controlLines) {
            return;
        }

        // Prepare
        const maxValue = scaleY.domain()[1];
        const sortedControlLines = [...controlLines].sort((a, b) => (a.value > b.value ? -1 : 1));
        const offsetHandles =
            sortedControlLines.length === 2 &&
            Math.abs(scaleY(sortedControlLines[1].value) - scaleY(sortedControlLines[0].value)) <= handleHeight * 2;

        // Parse control lines
        sortedControlLines.forEach((control, index) => {
            const { fixed, label, suffix } = control;
            const color = control.color || (theme.darkMode ? setup.colors.white : setup.colors.black);
            const draggable = !fixed && !!onControlLineDrag;
            const prefix = `${label}: ${control.prefix || ''}`;
            const value = transformValue(control.value, multiplier, decimals);
            const controlY = Math.round(Math.min(chartRect.bottom, scaleY(value) + chartRect.top));

            let handleY = -handleHeight / 2;
            if (offsetHandles) {
                handleY = index ? 0 : -handleHeight;
            } else if (controlY >= chartRect.bottom - handleHeight) {
                handleY = -handleHeight;
            }

            // Add element
            const element = container.append('g').style('cursor', draggable ? 'grab' : 'default');

            if (fixed) {
                element.lower();
            }

            // Add line
            const line = element
                .append('line')
                .attr('x1', 0)
                .attr('x2', chartRect.width)
                .style('stroke', color)
                .style('stroke-dasharray', 6)
                .style('stroke-width', 2);

            // Add handle
            const handle = element.append('g');

            // Add handle background
            const handleBackground = handle
                .append('rect')
                .attr('rx', 4)
                .attr('height', handleHeight)
                .style('fill', color);

            // Add handle label
            const handleLabel = handle
                .append('text')
                .attr('x', 10)
                .attr('y', Math.round(handleHeight / 2 + 4))
                .style('fill', theme.darkMode ? setup.colors.black : setup.colors.white)
                .style('font-size', 12)
                .style('font-weight', 'bold')
                .style('text-transform', 'uppercase');

            // Add drag indicator
            const dragIndicator = !draggable
                ? null
                : handle
                      .append('svg:image')
                      .attr('xlink:href', getUrl('/assets/images/handle-drag-indicator.svg'))
                      .attr('width', handleDragIndicatorWidth)
                      .attr('height', handleHeight);

            // Create updater
            const update = (value: number) => {
                element.attr('transform', `translate(${chartRect.left}, ${Math.round(scaleY(value) + chartRect.top)})`);
                handleLabel.text(formatValue(value, decimals, prefix, suffix));

                const handleWidth =
                    Math.ceil((handleLabel.node()?.getComputedTextLength() || 0) + 20) +
                    (draggable ? handleDragIndicatorWidth - 1 : 0);
                const handleX = chartRect.width + margin.right / 2 - handleWidth;

                handle.attr('transform', `translate(${handleX}, ${handleY})`);
                handleBackground.attr('width', handleWidth);
                line.attr('x2', handleX - 2);
                dragIndicator?.attr('x', handleWidth - handleDragIndicatorWidth + 1);
            };
            update(value);

            // Add drag
            if (draggable) {
                const drag = d3
                    .drag()
                    .on('start', e => {
                        // Get grab offset and starting value
                        e.target.grabOffset = e.y - controlY;
                        e.target.newValue = value;
                    })
                    .on('drag', e => {
                        // Get new value
                        const amount =
                            1 - _.clamp((e.y - chartRect.top - e.target.grabOffset) / chartRect.height, 0, 1);
                        const newValue = transformValue(amount, maxValue, decimals);

                        // Update handle
                        e.target.newValue = newValue;
                        update(newValue);
                    })
                    .on('end', e => {
                        const newValue = e.target.newValue / multiplier;
                        onControlLineDrag?.(newValue);
                    });
                drag(element as any);
            }
        });
    }, [chartRect, controlLines, decimals, multiplier, node, onControlLineDrag, scaleY, theme.darkMode]);

    useEffect(() => {
        // Empty hurricane bands container
        const container = node.select('.timeline-chart-hurricane-bands');
        container.selectChildren().remove();

        if (!hurricaneBands) {
            return;
        }

        // Add bands
        ['red', 'orange', 'yellow', 'green'].slice(4 - hurricaneBands).forEach(name => {
            const color = (setup.colors as any)[name];
            const points = (game.hurricaneBands as any)[name];
            const path = getHurricaneBandArea(points);
            container.append('path').attr('d', path).attr('fill', color);
        });
    }, [bars, chartRect, game.hurricaneBands, hurricaneBands, node, getHurricaneBandArea, scaleX, scaleY]);

    useEffect(() => {
        // Empty hurricane lines container
        const container = node.select('.timeline-chart-hurricane-lines');
        container.selectChildren().remove();

        if (!hurricaneLines) {
            return;
        }

        // Create path function
        const getPath = d3
            .line()
            .curve(d3.curveCardinal.tension(0.2))
            .x((d, i) => (scaleX(d[0]) || 0) + (i ? barStyles.width + barStyles.gap : 0) + chartRect.left)
            .y(d => scaleY(d[1]) + chartRect.top);

        // Add band
        if (!hurricaneBands) {
            const path = getHurricaneBandArea(game.hurricaneBands.red);
            container.append('path').attr('d', path).attr('fill', 'rgba(237, 239, 245, 0.5)');
        }

        // Parse lines
        ['WORST', 'BEST'].map((type, index) => {
            const points = game.hurricaneBands.red.map((day, i) => [i, day[index]] as [number, number]);
            const lastValues = points.slice(4).map(point => point[1]);
            const value = (index ? _.max(lastValues) : _.min(lastValues)) || 0;

            // Add element
            const element = container.append('g');

            // Add line
            const path = getPath(points);
            element
                .append('path')
                .attr('d', path)
                .style('fill', 'none')
                .style('stroke', setup.colors.info)
                .style('stroke-dasharray', 6)
                .style('stroke-width', 2);

            // Add handle
            const handle = element.append('g');

            // Add handle background
            const handleBackground = handle
                .append('rect')
                .attr('rx', 4)
                .attr('height', handleHeight)
                .style('fill', setup.colors.lightGrey);

            // Add handle label
            const handleLabel = handle
                .append('text')
                .attr('x', 10)
                .attr('y', 14)
                .style('fill', setup.colors.info)
                .style('font-size', 12)
                .style('font-weight', 'bold')
                .text(`${type} CASE PREDICTION: $${value.toLocaleString()}`);

            // Update handle sizes and positioning
            const handleWidth = Math.ceil((handleLabel.node()?.getBBox().width || 0) + 20);
            const handleX = chartRect.width - handleWidth + 60 + chartRect.left;
            const handleY = scaleY(value) + (index ? -handleHeight : 0) + chartRect.top;
            handle.attr('transform', `translate(${handleX}, ${handleY})`);
            handleBackground.attr('width', handleWidth);
        });
    }, [
        barsData,
        barStyles,
        chartRect,
        game.hurricaneBands,
        hurricaneBands,
        hurricaneLines,
        node,
        getHurricaneBandArea,
        scaleX,
        scaleY,
    ]);

    useEffect(() => {
        // Empty period lines container
        const container = node.select('.timeline-chart-period-lines');
        container.selectChildren().remove();

        if (!periodLabels || periodLabels.length < 2) {
            return;
        }

        // Get spacing and offset
        const spacing = chartRect.width / periodLabels.length;
        const offset = barStyles.gap / 2 + chartRect.left;

        // Add lines
        const lines = periodLabels.slice(0, periodLabels.length - 1);
        container
            .selectAll('line')
            .data(lines)
            .enter()
            .append('line')
            .attr('id', (_, i) => `timeline-chart-period-line-${i}`)
            .attr('x1', (_, i) => Math.round(spacing * (i + 1) + offset) - 0.5)
            .attr('x2', (_, i) => Math.round(spacing * (i + 1) + offset) - 0.5)
            .attr('y1', 0)
            .attr('y2', chartRect.bottom + 20)
            .style('stroke', theme.darkMode ? setup.colors.white : setup.colors.black)
            .style('stroke-width', 1);
    }, [barStyles.gap, chartRect, node, periodLabels, theme.darkMode]);

    useEffect(() => {
        // Empty variance container
        const container = node.select('.timeline-chart-variance');
        container.selectChildren().remove();

        if (!controlLines || controlLines.length < 2 || !variance) {
            return;
        }

        // Get values
        const value1 = controlLines[0].value;
        const value2 = controlLines[1].value;
        const difference = Math.abs(value1 - value2);
        const top = Math.max(value1, value2);

        // Add rectangle
        const rect = container
            .append('rect')
            .attr('x', chartRect.left)
            .attr('y', scaleY(top) + chartRect.top)
            .attr('height', chartRect.height - scaleY(difference))
            .attr('width', chartRect.width)
            .style('fill', 'rgba(237, 239, 245, 0.5)');

        // Animate
        if (variance === 'animate') {
            rect.attr('width', 0)
                .style('opacity', 0)
                .transition()
                .duration(1500)
                .attr('width', chartRect.width)
                .style('opacity', 1);
        }
    }, [chartRect, controlLines, node, variance, scaleY]);

    return (
        <Box>
            <svg height={height} ref={canvasRef} viewBox={`0 0 ${width} ${height}`} width="100%">
                <defs>
                    <clipPath id="timeline-chart-clip">
                        <rect
                            height={chartRect.height + 5}
                            width={chartRect.width + 10}
                            x={chartRect.left - 5}
                            y={chartRect.top - 5}
                        />
                    </clipPath>
                </defs>
                <g className="timeline-chart-period-lines" />
                <g className="timeline-chart-hurricane-bands" />
                <g className="timeline-chart-bars" clipPath="url(#timeline-chart-clip)" />
                <g className="timeline-chart-hurricane-lines" />
                <g className="timeline-chart-axes" />
                <g className="timeline-chart-variance" />
                <g className="timeline-chart-control-lines" />
            </svg>
            {periods && (
                <Box
                    sx={{
                        display: 'flex',
                        marginLeft: `${margin.left}px`,
                        marginRight: `${margin.right}px`,
                        position: 'relative',
                        top: 20 - margin.bottom,
                    }}
                >
                    {periodLabels?.map((periodLabel, index) => (
                        <Typography
                            align="center"
                            key={index}
                            variant="montserrat412"
                            sx={{
                                flex: 1,
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap'
                            }}>
                            {periodLabel}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}
